/**
 * LES COMPÉTENCES D'UNE HABILITATION, par niveau — module PUR.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demande de la RQ du 27/08/2026 : « dans le référentiel des habilitations, en
 * cliquant sur une habilitation, je veux voir la liste des compétences qui lui
 * sont rattachées… par niveau : routine, avancée, expert. »
 *
 * ── Ce que ce module NE stocke pas ─────────────────────────────────────────
 *
 * Rien. Il ASSEMBLE, à la lecture, ce que les grilles portent déjà : chaque
 * tâche déclare les habilitations dont elle fait partie
 * (`rh-grille-ligne.habilitations`, many-to-many depuis le lot B). Recopier ces
 * libellés dans une « description de l'habilitation » les figerait, et la fiche
 * dirait un an plus tard le contraire de la grille qu'elle prétend résumer.
 *
 * ── ⚠️ LE DEGRÉ EST CUMULATIF, pas une catégorie ───────────────────────────
 *
 * `routine` n'est pas « la case routine », c'est le SEUIL en dessous duquel on
 * ne tient pas le poste. Une personne experte est nécessairement au moins en
 * routine. Le compte affiché pour un degré est donc CUMULÉ — tout ce qu'il
 * faut savoir pour l'atteindre — tandis que la liste montrée sous chaque titre
 * ne contient que ce qui est exigé À ce degré précisément. Confondre les deux
 * ferait lire « 3 compétences pour être expert » là où il en faut trente-trois.
 *
 * ── ⚠️ UNE LISTE VIDE N'EST PAS « RIEN À ACQUÉRIR » ────────────────────────
 *
 * Cinq habilitations — management de la qualité, métrologie, stock, système
 * informatique, validation biologique — n'ont AUCUNE grille : le laboratoire ne
 * les a jamais décrites. L'écran doit dire cela, et non afficher un tableau
 * vide qui se lirait « il n'y a rien à savoir ». C'est `etatCompetences` qui
 * porte la phrase.
 */

export type Niveau = 'routine' | 'avance' | 'expert';

/** Du plus faible au plus fort — l'ordre donne son sens au cumul. */
export const NIVEAUX: Niveau[] = ['routine', 'avance', 'expert'];
export const LIBELLE_NIVEAU: Record<Niveau, string> = {
  routine: 'Routine', avance: 'Avancé', expert: 'Expert',
};

/** Rang d'un niveau. Un libellé inconnu vaut `routine` — le degré le plus bas,
 *  jamais le plus haut : une valeur illisible ne doit pas promouvoir. */
export const rang = (n: unknown): number => {
  const i = NIVEAUX.indexOf(String(n ?? '') as Niveau);
  return i < 0 ? 0 : i;
};

/** Une tâche, telle que le registre des grilles la sert. */
export interface TacheSource {
  code: string;
  libelle: string;
  section?: string;
  methode?: string;
  niveau?: string;
  actif?: boolean;
  ordre?: number;
  /** les habilitations dont elle fait partie */
  habilitationCodes?: string[] | null;
}

/** Une grille, réduite à ce dont ce module a besoin. */
export interface GrilleSource {
  code: string;
  libelle: string;
  lignes: TacheSource[];
}

export interface CompetenceFiche extends TacheSource {
  niveau: Niveau;
  grilleCode: string;
  grilleLibelle: string;
}

export interface BlocNiveau {
  niveau: Niveau;
  libelle: string;
  /** ce qui est exigé À ce degré précisément */
  competences: CompetenceFiche[];
  /** tout ce qu'il faut savoir POUR ce degré — celui-ci et ceux d'en dessous */
  cumul: number;
}

/**
 * Les compétences d'une habilitation, groupées par degré.
 *
 * Les tâches INACTIVES sont exclues : une tâche retirée du référentiel ne doit
 * plus être exigée de personne. Elle n'est pas supprimée pour autant — les
 * évaluations déjà portées sur elle restent lisibles ailleurs.
 *
 * ⚠️ Les trois degrés sont TOUJOURS rendus, même vides. Un poste sans tâche
 * « avancé » mais avec des tâches « expert » doit se lire comme tel : un trou
 * dans l'échelle est une information, pas une ligne à masquer.
 */
export function competencesDe(grilles: GrilleSource[], codeHabilitation: string): BlocNiveau[] {
  const cible = String(codeHabilitation || '').trim();
  const toutes: CompetenceFiche[] = [];
  if (cible) {
    for (const g of grilles || []) {
      for (const l of g.lignes || []) {
        if (l.actif === false) continue;
        if (!(l.habilitationCodes || []).includes(cible)) continue;
        toutes.push({
          ...l,
          niveau: NIVEAUX[rang(l.niveau)] as Niveau,
          grilleCode: g.code,
          grilleLibelle: g.libelle,
        });
      }
    }
  }
  /* L'ordre rendu est celui des grilles telles qu'elles arrivent, puis celui
     des tâches dans leur grille : deux impressions de la même fiche doivent
     être superposables. */
  toutes.sort((a, b) => a.grilleCode.localeCompare(b.grilleCode)
    || (a.ordre ?? 1e9) - (b.ordre ?? 1e9)
    || a.code.localeCompare(b.code));

  return NIVEAUX.map((n) => ({
    niveau: n,
    libelle: LIBELLE_NIVEAU[n],
    competences: toutes.filter((c) => c.niveau === n),
    cumul: toutes.filter((c) => rang(c.niveau) <= rang(n)).length,
  }));
}

/** Les grilles d'où proviennent les compétences — l'habilitation ne les
 *  possède pas, elle les emprunte, et la fiche doit dire d'où. */
export function grillesDe(blocs: BlocNiveau[]): { code: string; libelle: string; n: number }[] {
  const par = new Map<string, { code: string; libelle: string; n: number }>();
  for (const b of blocs) {
    for (const c of b.competences) {
      const e = par.get(c.grilleCode) || { code: c.grilleCode, libelle: c.grilleLibelle, n: 0 };
      e.n += 1;
      par.set(c.grilleCode, e);
    }
  }
  return [...par.values()].sort((a, b) => a.code.localeCompare(b.code));
}

export interface EtatCompetences {
  total: number;
  /** vrai quand aucune compétence n'est rattachée */
  vide: boolean;
  /** la phrase à afficher — jamais un tableau vide sans explication */
  message: string;
}

/**
 * Ce que la fiche peut dire, et ce qu'elle ne peut pas.
 *
 * Trois situations, trois phrases — et la distinction est tout l'intérêt de la
 * fiche en audit :
 *  · des compétences, réparties sur les degrés : rien à signaler ;
 *  · des compétences, mais TOUTES au même degré : l'échelle n'est pas
 *    renseignée, et « expert » ne veut alors rien dire de plus que « routine » ;
 *  · aucune compétence : personne n'a écrit ce qu'il faut savoir. Ce n'est PAS
 *    « rien à acquérir ».
 */
export function etatCompetences(blocs: BlocNiveau[]): EtatCompetences {
  const total = blocs.reduce((n, b) => n + b.competences.length, 0);
  if (!total) {
    return {
      total: 0,
      vide: true,
      message: 'Aucune compétence n’est rattachée à cette habilitation : personne n’a encore écrit '
        + 'ce qu’il faut savoir pour l’obtenir. Ce n’est pas « rien à acquérir » — c’est une grille '
        + 'qui reste à décrire, et un audit le demandera.',
    };
  }
  const pourvus = blocs.filter((b) => b.competences.length);
  if (pourvus.length === 1) {
    return {
      total,
      vide: false,
      message: `Les ${total} compétences sont toutes au degré « ${pourvus[0]!.libelle} » : l’échelle `
        + 'n’est pas renseignée. Tant qu’elle ne l’est pas, exiger un niveau supérieur sur un poste '
        + 'le rendrait intenable, puisque personne ne pourrait l’atteindre.',
    };
  }
  return { total, vide: false, message: '' };
}
